import React, { useState } from 'react';
import { Modal, Input, Button, Upload, Card, Row, Col, Spin, Empty, Pagination, message } from 'antd';
import { UploadOutlined } from '@ant-design/icons';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import apiClient from '../../api/axios';

export interface MediaPickerModalProps {
    open: boolean;
    onCancel: () => void;
    onSelect: (url: string) => void;
}

export const MediaPickerModal: React.FC<MediaPickerModalProps> = ({ open, onCancel, onSelect }) => {
    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(12);
    const [searchTerm, setSearchTerm] = useState('');
    const [uploading, setUploading] = useState(false);
    const queryClient = useQueryClient();

    const { data, isLoading } = useQuery({
        queryKey: ['adminMedia', page, pageSize, searchTerm],
        queryFn: async () => {
            const response = await apiClient.get('/admin/media', {
                params: { page, pageSize, searchTerm }
            });
            return response.data;
        },
        enabled: open,
    });

    const handleUpload = async (file: File) => {
        setUploading(true);
        const formData = new FormData();
        formData.append('file', file);
        formData.append('isPublic', 'true');
        formData.append('isActive', 'true');

        try {
            const response = await apiClient.post('/admin/media/upload', formData, {
                headers: { 'Content-Type': 'multipart/form-data' },
            });
            const dataObj = response.data?.data || response.data?.Data || response.data;
            const imgUrl = dataObj?.url || dataObj?.Url || dataObj?.filePath || dataObj?.FilePath;
            message.success('Bild erfolgreich in Mediathek hochgeladen');
            queryClient.invalidateQueries({ queryKey: ['adminMedia'] });
            if (imgUrl) {
                onSelect(imgUrl);
            }
        } catch (error: any) {
            message.error(error.response?.data?.message || 'Upload fehlgeschlagen');
        } finally {
            setUploading(false);
        }
        return false;
    };

    const mediaList = data?.data || [];

    return (
        <Modal
            title="Bild aus Mediathek auswählen"
            open={open}
            onCancel={onCancel}
            footer={null}
            width={850}
            destroyOnClose
        >
            <div className="flex justify-between items-center mb-4 gap-4 flex-wrap">
                <Input.Search
                    placeholder="Dateien durchsuchen..."
                    allowClear
                    onSearch={(v) => { setSearchTerm(v); setPage(1); }}
                    style={{ width: 280 }}
                />
                <Upload beforeUpload={handleUpload} showUploadList={false} maxCount={1}>
                    <Button icon={<UploadOutlined />} loading={uploading} type="primary">
                        Neues Bild hochladen
                    </Button>
                </Upload>
            </div>

            {isLoading ? (
                <div className="flex justify-center p-12">
                    <Spin size="large" />
                </div>
            ) : mediaList.length === 0 ? (
                <Empty description="Keine Bilder in der Mediathek gefunden" className="my-8" />
            ) : (
                <div style={{ maxHeight: '420px', overflowY: 'auto' }} className="pr-1">
                    <Row gutter={[12, 12]}>
                        {mediaList.map((item: any) => {
                            const isImg = item.contentType?.startsWith('image/') || /\.(jpg|jpeg|png|webp|gif|svg)$/i.test(item.filePath || item.originalName);
                            const url = item.filePath;
                            return (
                                <Col span={6} key={item.id}>
                                    <Card
                                        hoverable
                                        size="small"
                                        className="h-full flex flex-col justify-between cursor-pointer border hover:border-blue-500 transition-colors"
                                        onClick={() => onSelect(url)}
                                        cover={
                                            isImg ? (
                                                <div style={{ height: 110, background: '#f5f5f5', display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden' }}>
                                                    <img
                                                        src={url}
                                                        alt={item.originalName}
                                                        style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                                                    />
                                                </div>
                                            ) : (
                                                <div style={{ height: 110, background: '#e6f7ff', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                                    DOC
                                                </div>
                                            )
                                        }
                                    >
                                        <div className="text-xs truncate font-medium" title={item.originalName}>
                                            {item.originalName}
                                        </div>
                                        <Button
                                            type="primary"
                                            size="small"
                                            block
                                            className="mt-2"
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                onSelect(url);
                                            }}
                                        >
                                            Auswählen
                                        </Button>
                                    </Card>
                                </Col>
                            );
                        })}
                    </Row>
                </div>
            )}

            <div className="mt-4 flex justify-end">
                <Pagination
                    current={data?.pageNumber || page}
                    pageSize={data?.pageSize || pageSize}
                    total={data?.totalRecords || 0}
                    onChange={(p, s) => { setPage(p); setPageSize(s); }}
                    showSizeChanger
                    size="small"
                />
            </div>
        </Modal>
    );
};

export default MediaPickerModal;
